import { Component, OnInit } from '@angular/core';
import { DialogModel } from '@shared/models/dialog.model';
import { IDialogView } from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import { XatBotDataService } from '@features/xatbot/services/xat-bot-data.service';
import {
  ValidationNote,
  ValidationNoteCategory
} from './elite-form-validation-error.component';

interface Segment {
  kind: 'paragraph' | 'list';
  // For paragraph: a single string (newlines preserved via white-space: pre-line).
  // For list: an array of bullet items, leading "•"/"-"/"*" already stripped.
  text?: string;
  items?: string[];
}

// Body slot for OkCancelDialogComponent — title and Close button come from the
// platform shell. Receives { note: ValidationNote, ruleLabel: string } through
// DialogModel.input.
@Component({
  standalone: false,
  selector: 'app-elite-form-validation-notes-dialog-content',
  templateUrl: './elite-form-validation-notes-dialog-content.component.html',
  styleUrls: ['./elite-form-validation-notes-dialog-content.component.scss']
})
export class EliteFormValidationNotesDialogContentComponent implements OnInit, IDialogView {
  data: DialogModel;

  note: ValidationNote | null = null;
  ruleLabel = '';
  ruleId = '';
  summarySegments: ReadonlyArray<Segment> = [];
  detailSegments:  ReadonlyArray<Segment> = [];

  constructor(private xatBotDataService: XatBotDataService) {}

  ngOnInit(): void {
    const input = this.data?.input ?? {};
    this.note = (input.note as ValidationNote) ?? null;
    this.ruleLabel = (input.ruleLabel as string) ?? '';
    this.ruleId = (input.ruleId as string) ?? '';

    if (this.note) {
      this.summarySegments = parseSegments(this.note.summary);
      this.detailSegments  = parseSegments(this.note.detail);
    }
  }

  get category(): ValidationNoteCategory | null { return this.note?.category ?? null; }
  get categoryLabel(): string { return this.note?.categoryLabel ?? ''; }
  get issue(): string { return this.note?.issue ?? ''; }
  get question(): string { return this.note?.question ?? ''; }

  // Fires the pre-written suggested question at the floating XatBot, then closes
  // this dialog so the XatBot panel is unobstructed. The question embeds the
  // OECD code (e.g. "(70061)"), so the backend XmlAnalyzer resolves the matching
  // error and injects its authored resolution guidance.
  askXatBot(): void {
    const q = this.question.trim();
    if (q) this.xatBotDataService.openFloatingXatBotWithQuestion(q);
    // OkCancelDialogComponent stores its MatDialogRef on data['dialogRef'].
    (this.data as any)?.dialogRef?.close({ action: 'AskXatBot' });
  }

  // OkCancelDialog protocol — truthy return permits close.
  onOkClicked(): boolean { return true; }
}

// Splits raw note text into paragraph + bullet-list segments. Handles the
// catalogue convention where bullets are denoted by leading "•" (also accepts
// "-" / "*") and where a paragraph may include a header line followed by a
// run of bullets (e.g. "Example:\n  • A\n  • B").
function parseSegments(raw: string | null | undefined): ReadonlyArray<Segment> {
  const text = (raw ?? '').toString();
  if (!text.trim()) return [];
  const out: Segment[] = [];

  const paragraphs = text.split(/\n\s*\n/);
  for (const para of paragraphs) {
    const lines = para.split('\n');
    let textBuf: string[] = [];
    let listBuf: string[] | null = null;
    const flushText = () => {
      if (textBuf.length) { out.push({ kind: 'paragraph', text: textBuf.join('\n') }); textBuf = []; }
    };
    const flushList = () => {
      if (listBuf && listBuf.length) { out.push({ kind: 'list', items: listBuf }); }
      listBuf = null;
    };
    for (const rawLine of lines) {
      const trimmed = rawLine.replace(/\s+$/, '');
      if (!trimmed.trim()) continue;
      const m = /^\s*[•\-\*]\s+(.*)$/.exec(trimmed);
      if (m) {
        flushText();
        if (!listBuf) listBuf = [];
        listBuf.push(m[1]);
      } else {
        flushList();
        textBuf.push(trimmed.trim());
      }
    }
    flushList();
    flushText();
  }

  return out;
}
